import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Camera, CheckCircle2, Loader2, Truck } from 'lucide-react';
import { fetchDrivers, fetchTrucksForDriver, PublicMileageError, submitReading } from './api';
import type { PublicDriver, PublicReadingResult, PublicTruck } from './types';
import { LookupField } from '@/design-system/components/LookupField/LookupField';

/**
 * Public mileage page (company trucks, 2026-09-10; spec §5.3). Replaces
 * mileage.nadc1.net: open page, name pick, no login (decision 1). A driver on
 * more than one truck picks from a short list.
 *
 * No portal chrome, no links into the app, no session assumed — the same
 * footing as SmsOptInPage. Everything the page knows arrives by name from
 * /api/v1/public/mileage/*; no employee or user id is ever shown or sent.
 *
 * Offline-tolerant only in the sense of a clear error: nothing is queued.
 */

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const MAX_NOTE = 500;
const RETRY_LINE = 'That did not go through. Check your signal and try again.';

const field =
  'mt-1 w-full min-h-touch rounded-lg border border-graphite-700 bg-graphite-950 px-3 py-2 text-base text-graphite-100 placeholder-graphite-600 focus:border-safety-500 focus:outline-none focus:ring-1 focus:ring-safety-500 disabled:cursor-not-allowed disabled:opacity-50';

function miles(n: number | null | undefined): string {
  return n == null ? '—' : `${n.toLocaleString()} mi`;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "Apr 7, 2025" from the server's ISO timestamp; null when absent or unreadable. */
function dayLabel(iso: unknown): string | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function PublicMileagePage() {
  const [drivers, setDrivers] = useState<PublicDriver[] | null>(null);
  const [driversFailed, setDriversFailed] = useState(false);
  const [driver, setDriver] = useState('');

  // null = no driver picked yet; 'loading' while the truck list is in flight.
  const [trucks, setTrucks] = useState<PublicTruck[] | 'loading' | null>(null);
  const [truckId, setTruckId] = useState('');

  const [reading, setReading] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PublicReadingResult | null>(null);

  // Michael 2026-10-07: "a checkbox to override the large mileage entry some
  // of these guys are encountering due to not inputting in a very long time."
  // bigJump is the reading the server refused as too high but would take
  // with a tick (details.confirmable). It and the tick are cleared by any
  // edit to the reading, truck or name, and by every submit, so a tick only
  // ever confirms the number the box names; the box is never pre-ticked.
  const [bigJump, setBigJump] = useState<number | null>(null);
  const [jumpConfirmed, setJumpConfirmed] = useState(false);

  const photoInput = useRef<HTMLInputElement>(null);
  // trucks low-fix pass 2026-09-10: focus target after a submit error, so a
  // screen reader lands on the field the error describes instead of the
  // disabled-then-blurred Submit button (or body).
  const readingInput = useRef<HTMLInputElement>(null);
  // 2026-10-07: after a confirmable refusal the next step is the tick, so
  // focus goes to the box (described by the explanation) — not the reading,
  // whose keypad would cover the box and Submit on a phone.
  const confirmInput = useRef<HTMLInputElement>(null);
  // Guards a slow truck fetch from landing after the driver changed again.
  const truckRequest = useRef(0);

  useEffect(() => {
    let active = true;
    fetchDrivers()
      .then((list) => { if (active) setDrivers(list); })
      .catch(() => { if (active) { setDrivers([]); setDriversFailed(true); } });
    return () => { active = false; };
  }, []);

  const truckList = Array.isArray(trucks) ? trucks : [];
  const truck = truckList.find((t) => t.id === truckId) ?? null;

  // The error line and a confirmable jump's box (2026-10-07) go together.
  function clearRefusal() {
    setError(null);
    setBigJump(null);
    setJumpConfirmed(false);
  }

  function chooseDriver(name: string) {
    setDriver(name);
    setTruckId('');
    clearRefusal();
    if (!name) { setTrucks(null); return; }
    const seq = ++truckRequest.current;
    setTrucks('loading');
    fetchTrucksForDriver(name)
      .then((list) => {
        if (seq !== truckRequest.current) return;
        setTrucks(list);
        if (list.length === 1) setTruckId(list[0].id);
      })
      .catch(() => {
        if (seq !== truckRequest.current) return;
        setTrucks(null);
        setError(RETRY_LINE);
      });
  }

  function choosePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_PHOTO_BYTES) {
      setPhoto(null);
      setPhotoError('That photo is over 5 MB.');
      e.target.value = '';
      return;
    }
    setPhotoError(null);
    setPhoto(file);
  }

  function reset() {
    setDriver('');
    setTrucks(null);
    setTruckId('');
    setReading('');
    setPhoto(null);
    setPhotoError(null);
    setNote('');
    clearRefusal();
    setResult(null);
    setState('idle');
    if (photoInput.current) photoInput.current.value = '';
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!truck || reading === '') return;
    const sent = Number(reading);
    // Edits clear the box, so a standing tick is for this very number.
    const confirm = jumpConfirmed && bigJump === sent;
    clearRefusal();
    setState('sending');
    try {
      const saved = await submitReading({
        truck_id: truck.id,
        driver,
        reading: sent,
        note: note.trim(),
        photo,
        confirm_large_jump: confirm,
      });
      setResult(saved);
      setState('done');
    } catch (err) {
      let message = RETRY_LINE;
      let jump: number | null = null;
      if (err instanceof PublicMileageError && err.status === 422 && err.code === 'READING_TOO_LOW') {
        const latest = err.details?.latest;
        const shown = typeof latest === 'number' ? latest.toLocaleString() : String(latest ?? '');
        message = `Your last reading was ${shown}. Enter that or higher.`;
      } else if (err instanceof PublicMileageError && err.status === 422 && err.code === 'READING_TOO_HIGH') {
        // trucks fix pass 2026-09-10: the server refuses an implausible
        // jump over the latest; say so, the retry line would loop the driver.
        const latest = err.details?.latest;
        const shown = typeof latest === 'number' ? latest.toLocaleString() : String(latest ?? '');
        if (err.details?.confirmable === true && typeof latest === 'number') {
          // 2026-10-07: a jump a tick would let through — most often a
          // driver who has not entered miles in months. Explain it and
          // offer the box instead of sending them to the office.
          const lastRead = dayLabel(err.details?.last_read_at);
          jump = sent;
          message =
            `That is ${(sent - latest).toLocaleString()} miles more than the last reading ` +
            `(${shown}${lastRead ? ` on ${lastRead}` : ''}). ` +
            'If it has been a long time since you entered your miles, tick the box and press Submit again.';
        } else {
          message = `That is a long way above your last reading (${shown}). Check the number; if it is right, ask the office to enter it.`;
        }
      }
      // trucks low-fix pass 2026-09-10: flush the re-enable before focusing —
      // the input is disabled while sending, and focus() on a disabled
      // element is a no-op.
      flushSync(() => {
        setError(message);
        setBigJump(jump);
        setState('idle');
      });
      (jump !== null ? confirmInput : readingInput).current?.focus();
    }
  }

  const header = (
    <p className="mb-1 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-red-500">NADC · DemoOps</p>
  );

  if (state === 'done' && result) {
    return (
      <div data-industrial className="ui-ind-paint min-h-screen bg-graphite-950 px-4 py-10 text-graphite-100">
        <div className="mx-auto max-w-md text-center">
          {header}
          <CheckCircle2 className="mx-auto h-12 w-12 text-signal-500" aria-hidden />
          <h1 className="mt-4 text-xl font-bold">Mileage logged</h1>
          <div className="mt-5 rounded-xl border border-graphite-700 bg-graphite-900 p-4 text-left">
            <p className="text-sm text-graphite-400">Truck</p>
            <p className="text-base font-semibold">
              {result.truck.name}
              <span className="ml-2 text-sm font-normal text-graphite-400">{result.truck.asset_number}</span>
            </p>
            <p className="mt-3 text-sm text-graphite-400">Reading</p>
            <p className="text-2xl font-bold tabular-nums">{miles(result.reading)}</p>
            <p className="mt-3 text-sm text-graphite-400">Time</p>
            <p className="text-base">{whenLabel(result.read_at)}</p>
          </div>
          <button
            type="button"
            onClick={reset}
            className="mt-6 flex min-h-touch w-full items-center justify-center rounded-xl bg-safety-500 px-4 py-3 font-semibold text-white transition hover:bg-safety-400"
          >
            Enter another
          </button>
        </div>
      </div>
    );
  }

  const driversLoading = drivers === null;
  // trucks low-fix pass 2026-09-10: the "Current" hint and the error line both
  // describe the reading input; announce them with it.
  const readingDescribedBy = [truck && 'reading-hint', error && 'reading-error'].filter(Boolean).join(' ') || undefined;

  return (
    <div data-industrial className="ui-ind-paint min-h-screen bg-graphite-950 px-4 py-8 text-graphite-100">
      <div className="mx-auto max-w-md">
        {header}
        <h1 className="text-center text-xl font-bold">Truck mileage</h1>
        <p className="mt-1 text-center text-sm text-graphite-400">Enter the odometer reading on your company truck.</p>

        <form onSubmit={submit} className="mt-5 space-y-5 rounded-xl border border-graphite-700 bg-graphite-900 p-4">
          {/* Step 1: your name */}
          <div>
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-graphite-500" aria-hidden>1.</span>
              <label htmlFor="driver" className="font-medium text-graphite-200">Your name</label>
            </div>
            <LookupField
              id="driver"
              disabled={driversLoading || state === 'sending'}
              value={driver}
              onChange={chooseDriver}
              options={(drivers ?? []).map((d) => ({ value: d.name, label: d.name }))}
              emptyLabel={driversLoading ? undefined : 'Choose your name'}
              placeholder={driversLoading ? 'Loading names…' : 'Choose your name'}
              inputClassName={field}
            />
            {driversFailed && (
              <p className="mt-1 text-xs text-danger-300">Could not load the driver list. Check your signal and reload.</p>
            )}
          </div>

          {/* Step 2: your truck */}
          <div>
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-graphite-500" aria-hidden>2.</span>
              <p className="font-medium text-graphite-200">Your truck</p>
            </div>
            {trucks === null && (
              <p className="mt-1 text-sm text-graphite-500">Pick your name first.</p>
            )}
            {trucks === 'loading' && (
              <p className="mt-1 flex items-center gap-2 text-sm text-graphite-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Finding your truck…
              </p>
            )}
            {Array.isArray(trucks) && trucks.length === 0 && (
              <p className="mt-1 rounded-lg border border-safety-500/40 bg-safety-500/10 p-3 text-sm text-safety-300">
                No truck assigned. Contact your supervisor.
              </p>
            )}
            {Array.isArray(trucks) && trucks.length === 1 && truck && (
              <div className="mt-1 flex items-center gap-3 rounded-lg border border-safety-500/50 bg-safety-500/10 px-3 py-3">
                <Truck className="h-5 w-5 shrink-0 text-safety-500" aria-hidden />
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold">{truck.name}</p>
                  <p className="text-xs text-graphite-400">{truck.asset_number}</p>
                </div>
              </div>
            )}
            {Array.isArray(trucks) && trucks.length > 1 && (
              <fieldset className="mt-1 space-y-2">
                <legend className="sr-only">Choose your truck</legend>
                {trucks.map((t) => {
                  const active = t.id === truckId;
                  return (
                    <label
                      key={t.id}
                      className={`flex min-h-touch cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 ${
                        active ? 'border-safety-500 bg-safety-500/10' : 'border-graphite-700 bg-graphite-950'
                      }`}
                    >
                      <input
                        type="radio"
                        name="truck"
                        value={t.id}
                        checked={active}
                        onChange={() => { setTruckId(t.id); clearRefusal(); }}
                        className="h-5 w-5 shrink-0 border-graphite-600 bg-graphite-950 text-safety-500 focus:ring-safety-500"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-base font-semibold">{t.name}</span>
                        <span className="block text-xs text-graphite-400">
                          {t.asset_number}
                          {t.mileage != null ? ` · ${miles(t.mileage)}` : ''}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}
          </div>

          {/* Step 3: reading */}
          <div>
            <div className="flex items-baseline gap-2 text-sm">
              <span className="text-graphite-500" aria-hidden>3.</span>
              <label htmlFor="reading" className="font-medium text-graphite-200">Reading</label>
            </div>
            <input
              ref={readingInput}
              id="reading"
              name="reading"
              type="number"
              inputMode="numeric"
              min={0}
              max={999999}
              step={1}
              required
              autoComplete="off"
              value={reading}
              disabled={!truck || state === 'sending'}
              onChange={(e) => { setReading(e.target.value); clearRefusal(); }}
              aria-describedby={readingDescribedBy}
              className={`${field} text-2xl tabular-nums`}
              placeholder="Odometer miles"
            />
            {truck && (
              <p id="reading-hint" className="mt-1 text-sm text-graphite-400">Current: {miles(truck.mileage)}</p>
            )}
          </div>

          {/* Photo (optional) */}
          <div>
            <label htmlFor="photo" className="text-sm font-medium text-graphite-200">
              Photo of the odometer <span className="font-normal text-graphite-500">(optional)</span>
            </label>
            <label
              htmlFor="photo"
              className="mt-1 flex min-h-touch cursor-pointer items-center gap-2 rounded-lg border border-dashed border-graphite-600 px-3 py-2 text-sm text-graphite-300"
            >
              <Camera className="h-5 w-5 shrink-0 text-graphite-400" aria-hidden />
              <span className="truncate">{photo ? photo.name : 'Take a photo'}</span>
            </label>
            <input
              ref={photoInput}
              id="photo"
              name="photo"
              type="file"
              accept="image/*"
              capture="environment"
              disabled={!truck || state === 'sending'}
              onChange={choosePhoto}
              className="sr-only"
            />
            {photoError && <p className="mt-1 text-xs text-danger-300">{photoError}</p>}
          </div>

          {/* Note (optional) */}
          <div>
            <label htmlFor="note" className="text-sm font-medium text-graphite-200">
              Note <span className="font-normal text-graphite-500">(optional)</span>
            </label>
            <textarea
              id="note"
              name="note"
              rows={2}
              maxLength={MAX_NOTE}
              value={note}
              disabled={!truck || state === 'sending'}
              onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE))}
              className={field}
              placeholder="Anything the office should know"
            />
            <p className="mt-1 text-right text-xs tabular-nums text-graphite-500">{note.length}/{MAX_NOTE}</p>
          </div>

          {error && bigJump === null && (
            <p id="reading-error" role="alert" className="rounded-lg border border-danger-500/40 bg-danger-500/10 p-3 text-sm text-danger-300">
              {error}
            </p>
          )}

          {/* 2026-10-07: a jump the driver may confirm — a question, not an error. */}
          {error && bigJump !== null && (
            <div className="space-y-3 rounded-lg border border-safety-500/40 bg-safety-500/10 p-3">
              <p id="reading-error" role="alert" className="text-sm text-safety-300">
                {error}
              </p>
              <label className="flex min-h-touch cursor-pointer items-center gap-3">
                <input
                  ref={confirmInput}
                  type="checkbox"
                  name="confirm_large_jump"
                  checked={jumpConfirmed}
                  onChange={(e) => setJumpConfirmed(e.target.checked)}
                  aria-describedby="reading-error"
                  className="h-5 w-5 shrink-0 accent-safety-500"
                />
                <span className="text-base font-semibold text-graphite-100">Yes, {bigJump.toLocaleString()} is right</span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={!truck || reading === '' || state === 'sending'}
            className="flex min-h-touch w-full items-center justify-center gap-2 rounded-xl bg-safety-500 px-4 py-3 font-semibold text-white transition hover:bg-safety-400 disabled:cursor-not-allowed disabled:bg-graphite-700 disabled:text-graphite-500"
          >
            {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Submit reading
          </button>
        </form>
      </div>
    </div>
  );
}

export default PublicMileagePage;
